<template>

  <v-container class="pa-0 ma-0 step delivery-step-head-to-receiver" v-if="hq && route && vehicle">
    <!-- Complete message -->
    <v-row class="pa-0 ma-0 complete-message">
      <v-col sm="12" style="font-size:16px" class="ma-0 pa-0 content-accent">
        Your delivery is done!
      </v-col>
    </v-row>

    <!-- HQ info -->
    <v-row class="pa-0 ma-0 mt-8">
      <v-col sm="12" class="ma-0 pa-0 content-title">
        Return the vehicle to:
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0 mt-4">
      <v-col sm="12" class="ma-0 pa-0 content-accent">{{ hq.name }}</v-col>
    </v-row>
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="ma-0 pa-0 pt-2 address-text d-flex align-center">
        <div>
          <div>{{ formatAddress(hq).street_1 }}</div>
          <div v-if="formatAddress(hq).street_2">{{ formatAddress(hq).street_2 }}</div>
          <div v-if="formatAddress(hq).area">{{ formatAddress(hq).area }}</div>
        </div>
        <v-btn plain color="primary" class="ml-6" @click="copyAddress">
          <v-icon class="icon-copy">mdi-content-copy</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Returning to hq button -->
    <v-row class="pa-0 ma-0 mt-8">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column align-left justify-center">
        <v-btn color="primary" v-if="isReturningToHqProcessing">
          <v-progress-circular indeterminate :size="20" color="white" />
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompletedReturningToHq">
          En route at {{ formatDateTime(hasCompletedReturningToHq) }}
        </v-btn>
        <double-click-confirm-button
            v-else
            :buttonText="`Returning to ${hq.name}`"
            confirmTitle="Ready to head back?"
            :disabled="isReturningToHq || isCompleted"
            @confirmed="returnToHq" />
      </v-col>
    </v-row>
    <v-row class="pa-0 ma-0 mt-2">
      <v-col sm="12" class="ma-0 pa-0 instructions-text" v-if="showDrivingDirections">
        (click to get directions)
      </v-col>
    </v-row>

    <!-- directions link -->
    <v-row class="pa-0 ma-0 mt-6">
      <v-col sm="12" class="ma-0 pa-0">
        <v-btn text color="primary" :disabled="!isReturningToHq" v-if="showDrivingDirections">
          <v-icon class="icon-open-in-new">mdi-open-in-new</v-icon>
          <span>Driving directions</span>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Odometer reading -->
    <v-row class="pa-0 ma-0 mt-6">
      <v-col cols="12" xs="12" sm="6" md="6" lg="5" class="ma-0 px-0 pt-2 pb-0">
        <span class="input-label-text">Odometer reading (optional)</span>
        <v-text-field
            outlined
            clearable
            maxlength="6"
            class="mt-2"
            :disabled="!isReturningToHq"
            v-model.trim="odometerReading" />
      </v-col>
    </v-row>

    <!-- Complete step -->
    <v-row class="pa-0 ma-0 complete-step">
      <v-col sm="12" class="pa-0 ma-0 d-flex flex-column justify-center">
        <v-btn color="primary" v-if="isAtHqProcessing">
          <v-progress-circular indeterminate :size="20" color="white" />
        </v-btn>
        <v-btn
            large
            color="primary"
            :disabled="true"
            v-else-if="hasCompletedArrivalAtHq">
          Arrived at {{ formatDateTime(hasCompletedArrivalAtHq) }}
        </v-btn>
        <double-click-confirm-button
            v-else
            :buttonText="`Arrived at ${hq.name}`"
            confirmTitle="Vehicle has been returned."
            :disabled="!isReturningToHq"
            @confirmed="completeStep" />
      </v-col>
    </v-row>

  </v-container>

</template>

<script>
import DoubleClickConfirmButton from '@/components/app/DoubleClickConfirmButton'

export default {
  name: 'DeliveryStepReturnToHq',

  components: {
    DoubleClickConfirmButton
  },

  props: {
    hq: {
      type: Object,
      required: true
    },
    route: {
      type: Object,
      required: true
    },
    stepNumber: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      isReturningToHqProcessing: false,
      isAtHqProcessing: false,
      showDrivingDirections: false,
      vehicle: {},
      odometerReading: null
    }
  },

  computed: {
    isReturningToHq () {
      return this.route.route_status === 'RETURNING_TO_HQ'
    },
    isCompleted () {
      return this.route.route_status === 'COMPLETED'
    },
    hasCompletedReturningToHq () {
      return this.route.returning_to_hq_at
    },
    hasCompletedArrivalAtHq () {
      return this.route.completed_at
    }
  },

  async beforeMount () {
    this.odometerReading = this.route.odometer_reading_end
    if (this.route.vehicle_id) {
      this.vehicle = await this.$store.dispatch('sh/fetchData', { resource: 'vehicle', id: this.route.vehicle_id })
    }
  },

  methods: {
    // copy address to clipboard
    copyAddress () {
      this.$root.copyToClipboard(this.$root.utils.getAddressTextFromData(this.hq))
    },

    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.dateFormat(date, 'MMM D h:mma') : ''
    },

    formatAddress (data = {}) {
      return this.$root.utils.getAddressTextFromData(data, { multi_lines: true })
    },

    // Set vehicle: returning to lot
    async returnToHq () {
      this.isReturningToHqProcessing = true

      try {
        await this.$store.dispatch('sh/returnToLot', this.route)
      } catch (err) {
        console.log('Error setting route: to lot =>', err)
      }

      this.isReturningToHqProcessing = false
    },

    // Set to: at hq, save odometer reading, and complete step
    async completeStep () {
      this.isAtHqProcessing = true

      try {
        const data = {
          routeId: this.route.id,
          odometerReading: this.odometerReading
        }
        await this.$store.dispatch('sh/completeDelivery', data)
        this.$emit('completed')
      } catch (err) {
        console.log('Error completing delivery =>', err)
      }

      this.isAtHqProcessing = false

      window.scrollTo(0, 0) // scroll to top of stepper
    }
  }
}
</script>

<style>
</style>
